import React, { useEffect, useRef } from 'react';
import { Calendar, Droplet, FileCheck } from 'lucide-react';

const HowItWorksSection = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.fade-up').forEach((el, index) => {
              setTimeout(() => { el.classList.add('visible'); }, index * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const steps = [
    {
      number: '01',
      icon: Calendar,
      title: 'Book online',
      description: 'Choose your time slot and address. We come to you.',
    },
    {
      number: '02',
      icon: Droplet,
      title: 'We collect',
      description: 'Our qualified phlebotomist arrives with all equipment. Collection takes 10–15 minutes.',
    },
    {
      number: '03',
      icon: FileCheck,
      title: 'Results to your GP',
      description: 'Specimens delivered to the lab. Results sent directly to your doctor.',
    },
  ];

  return (
    /* Band 1: off-white — contrasts with the dark hero above */
    <section
      id="how-it-works"
      ref={sectionRef}
      className="relative overflow-hidden"
      style={{ background: '#F7F9FB', paddingTop: '88px', paddingBottom: '88px' }}
    >
      <div className="container relative z-10">
        <div className="text-center mb-14 fade-up">
          <span className="pill-label">Simple Process</span>
          <h2 className="text-[#00001E] mt-2 mb-4">How it works</h2>
          <p className="text-[#2B3879] text-lg max-w-2xl mx-auto">
            Three easy steps to professional blood collection at your door.
          </p>
        </div>

        {/* Cards on white — lift off the off-white section bg */}
        <div className="grid md:grid-cols-3 gap-7 max-w-5xl mx-auto">
          {steps.map((step) => {
            const Icon = step.icon;
            return (
              <div key={step.title} className="fade-up">
                <div
                  className="text-center h-full flex flex-col items-center rounded-2xl p-8 bg-white transition-all duration-300"
                  style={{
                    border: '1px solid rgba(43,56,121,0.08)',
                    boxShadow: '0 4px 20px rgba(43,56,121,0.08), 0 1px 4px rgba(43,56,121,0.04)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 12px 40px rgba(43,56,121,0.13), 0 2px 8px rgba(43,56,121,0.06)';
                    e.currentTarget.style.borderColor = 'rgba(128,229,203,0.45)';
                    e.currentTarget.style.transform = 'translateY(-4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(43,56,121,0.08), 0 1px 4px rgba(43,56,121,0.04)';
                    e.currentTarget.style.borderColor = 'rgba(43,56,121,0.08)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {/* Step circle — mint-tinted with bold number */}
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center mb-5"
                    style={{
                      background: 'rgba(128,229,203,0.18)',
                      border: '2px solid rgba(128,229,203,0.55)',
                    }}
                  >
                    <span
                      className="font-bold text-xl"
                      style={{ color: '#00001E', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}
                    >
                      {step.number}
                    </span>
                  </div>

                  {/* Icon — navy */}
                  <div className="mb-4">
                    <Icon className="w-9 h-9 mx-auto" style={{ color: '#2B3879' }} />
                  </div>

                  {/* Thin mint accent line */}
                  <div
                    className="w-8 h-0.5 rounded-full mx-auto mb-4"
                    style={{ background: '#80E5CB' }}
                  />

                  <h3
                    className="font-semibold mb-3"
                    style={{ color: '#00001E', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", fontSize: '1.05rem' }}
                  >
                    {step.title}
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: '#2B3879' }}>
                    {step.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
